import { DomainList, DomainListItemCard } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { useDeleteProvider, useGetProviders } from "@/hooks/providers";
import { T } from "@/i18n";
import { formatPhoneNumber } from "@/lib/utils";
import { Provider } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { ConciergeBell, User } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(providers)/providers")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetProviders();

  return (
    <DomainList
      domain="providers"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="providers.title" /> <User width={16} height={16} />
            </>
          ),
          href: "/providers",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "providers.fields.name",
          },
          {
            header: "providers.fields.phone",
          },
          {
            header: "providers.fields.email",
          },
          {
            header: "providers.fields.birthdate",
          },
          {
            header: "providers.fields.country",
          },
          {
            header: "providers.fields.language",
          },
          {
            header: "providers.fields.paymentAfterInvoice",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/providers/new"
      renderItem={(provider, index, isTable) => (
        <ProviderCard provider={provider} key={index} isTable={isTable} />
      )}
    />
  );
}

function ProviderCard({
  provider,
  isTable,
}: {
  provider: Provider;
  isTable: boolean;
}) {
  const { mutateAsync } = useDeleteProvider();
  const deleteProvider = () => mutateAsync(provider.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      domain="providers"
      table={isTable}
      title={provider.name}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={formatPhoneNumber(provider.phone)}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      viewLink={(children, key) => (
        <Link
          key={key}
          to="/providers/$id"
          params={{ id: provider.id.toString() }}
        >
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/providers/$id/edit"
          params={{ id: provider.id.toString() }}
        >
          {children}
        </Link>
      )}
      extraItems={[
        {
          label: "serviceProviders.provider.title",
          icon: () => <ConciergeBell />,
          itemWrapper: ({ children, key }) => (
            <Link
              key={key}
              to="/service-providers/$id"
              search={{ domain: "provider" }}
              params={{ id: provider.id.toString() }}
            >
              {children}
            </Link>
          ),
        },
      ]}
      contentMap={[
        {
          value: provider.email,
          tableClassName: "whitespace-nowrap",
        },
        {
          label: t("providers.fields.birthdate"),
          value: provider.birthdate
            ? format(new Date(provider.birthdate), "PPP")
            : "-",
        },
        {
          label: t("providers.fields.country"),
          value: provider.country ? (
            <T tKey={`countries.${provider.country}`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("providers.fields.language"),
          tableClassName: "min-w-48",
          value: !provider.language?.length ? (
            "-"
          ) : (
            <div className="flex flex-wrap items-center gap-1">
              {provider.language?.map((l) => (
                <Badge className="whitespace-nowrap" key={l}>
                  {l}
                </Badge>
              ))}
            </div>
          ),
        },
        {
          label: t("providers.fields.paymentAfterInvoice"),
          value:
            typeof provider.paymentAfterInvoice !== "boolean" ? (
              "-"
            ) : (
              <T tKey={`common.boolean.${provider.paymentAfterInvoice}`} />
            ),
        },
      ]}
      onDelete={deleteProvider}
    />
  );
}
